import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, Waves } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { EmptyState } from '../components/atoms/EmptyState';
import { Grid } from '../components/atoms/Grid/Grid';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Stack } from '../components/atoms/Stack/Stack';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../components/ui/Dialog/Dialog';
import { NewFlowSurveyForm } from '../components/flow/NewFlowSurveyForm';
import { PumpScheduleEditor } from '../components/flow/PumpScheduleEditor';
import { PlacementAssistant } from '../components/flow/PlacementAssistant';
import { useAquariums } from '../hooks/useAquariums';
import { formatSurveyDate } from '../services/lightSurveyService';
import {
  FLOW_LEVELS,
  equipmentPumpOptions,
  flowSchedulesKey,
  flowSurveysKey,
  listFlowSchedules,
  listFlowSurveys,
  type FlowSurveySummary,
} from '../services/flowSurveyService';
import '../components/lighting/Lighting.css';
import '../components/flow/FlowMap.css';

function SurveyCard({ survey, aquariumId }: { survey: FlowSurveySummary; aquariumId: string }) {
  const { t, i18n } = useTranslation();
  return (
    <li>
      <Link className="lighting__survey-link" to={`/aquariums/${encodeURIComponent(aquariumId)}/flow/${encodeURIComponent(survey.id)}`}>
        <Card>
          <Stack gap="3">
            {survey.imageUrl ? (
              <img className="lighting__thumb" src={survey.imageUrl} alt="" loading="lazy" />
            ) : (
              <span className="lighting__thumb lighting__thumb--empty" aria-hidden="true" />
            )}
            <Heading level={3}>{survey.title}</Heading>
            <Text color="secondary" size="sm">
              {formatSurveyDate(survey.measuredAt, i18n.language || 'de')}
              {survey.measuredClock && ` · ${survey.measuredClock}`}
            </Text>
            <Text size="sm">{t('flow.list.counts', { pumps: survey.pumpCount, points: survey.pointCount, corals: survey.coralCount })}</Text>
          </Stack>
        </Card>
      </Link>
    </li>
  );
}

/**
 * Strömung / flow: the aquarium's flow surveys ("Strömungskarten") — pumps,
 * flow readings as ranges and corals on a photo, and whether each coral sits in
 * the flow its group needs. Starting a survey; how to read flow without a meter.
 */
export function FlowPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const [open, setOpen] = useState(false);
  const query = useQuery({ queryKey: flowSurveysKey(aquariumId), queryFn: () => listFlowSurveys(aquariumId), enabled: Boolean(aquariumId) });
  const surveys = query.data ?? [];
  const schedules = useQuery({ queryKey: flowSchedulesKey(aquariumId), queryFn: () => listFlowSchedules(aquariumId), enabled: Boolean(aquariumId) });
  const aquariums = useAquariums();
  // The aquarium's flow and return pumps, plus any pump that already has a schedule (matched by name).
  const pumpNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const p of equipmentPumpOptions(aquariums.data?.find((a) => a.id === aquariumId))) names.set(p.name.toLowerCase(), p.name);
    for (const s of schedules.data ?? []) if (!names.has(s.pumpLabel.toLowerCase())) names.set(s.pumpLabel.toLowerCase(), s.pumpLabel);
    return [...names.values()];
  }, [aquariums.data, aquariumId, schedules.data]);

  let scheduleBody;
  if (schedules.isPending) {
    scheduleBody = <LiveRegion busy><Text color="muted">{t('flow.schedule.loading')}</Text></LiveRegion>;
  } else if (schedules.isError) {
    scheduleBody = <LoadErrorState title={t('flow.schedule.loadError')} onRetry={() => void schedules.refetch()} retryLabel={t('flow.retry')} titleLevel={3} />;
  } else if (pumpNames.length === 0) {
    scheduleBody = (
      <EmptyState
        icon={<Clock aria-hidden="true" />}
        headline={t('flow.schedule.emptyTitle')}
        description={t('flow.schedule.emptyDescription')}
        actionLabel={t('flow.new.open')}
        actionVariant="secondary"
        onAction={() => setOpen(true)}
        headingLevel={3}
      />
    );
  } else {
    scheduleBody = (
      <Stack gap="4" data-testid="flow-schedules">
        {pumpNames.map((name) => {
          const schedule = schedules.data?.find((s) => s.pumpLabel.toLowerCase() === name.toLowerCase()) ?? null;
          return (
            <PumpScheduleEditor
              key={`${name}-${schedule?.updatedAt ?? 'new'}`}
              aquariumId={aquariumId}
              pumpLabel={name}
              schedule={schedule}
              onChanged={() => void schedules.refetch()}
            />
          );
        })}
      </Stack>
    );
  }

  let body;
  if (query.isPending) {
    body = <LiveRegion busy><Text color="muted">{t('flow.loading')}</Text></LiveRegion>;
  } else if (query.isError) {
    body = <LoadErrorState title={t('flow.loadError')} onRetry={() => void query.refetch()} retryLabel={t('flow.retry')} titleLevel={3} />;
  } else if (surveys.length === 0) {
    body = (
      <EmptyState
        icon={<Waves aria-hidden="true" />}
        headline={t('flow.empty.title')}
        description={t('flow.empty.description')}
        actionLabel={t('flow.new.open')}
        actionVariant="secondary"
        onAction={() => setOpen(true)}
        headingLevel={3}
      />
    );
  } else {
    body = (
      <Grid as="ul" minItemWidth="sm" gap="4" className="lighting__grid" data-testid="flow-surveys">
        {surveys.map((s) => <SurveyCard key={s.id} survey={s} aquariumId={aquariumId} />)}
      </Grid>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader title={t('flow.title')} description={t('flow.subtitle')} actions={<Button onClick={() => setOpen(true)}>{t('flow.new.open')}</Button>} />
          <Section title={t('flow.section.surveys')}>{body}</Section>
          <Section title={t('flow.placement.title')} description={t('flow.placement.description')}>
            <PlacementAssistant aquariumId={aquariumId} />
          </Section>
          <Section title={t('flow.schedule.title')} description={t('flow.schedule.description')}>{scheduleBody}</Section>
          <Section title={t('flow.howTo.title')} description={t('flow.howTo.intro')}>
            <Stack gap="4">
              <Card>
                <Stack gap="3">
                  <Heading level={3}>{t('flow.howTo.scaleTitle')}</Heading>
                  <ul className="flow-map__levels" data-testid="flow-scale">
                    {FLOW_LEVELS.map((level) => (
                      <li key={level} className="flow-map__legend-item">
                        <span className={`flow-map__swatch flow-map__cell--${level}`} aria-hidden="true" />
                        <Text size="sm">{t('flow.levels.option', { level, name: t(`flow.levels.${level}.name`), sign: t(`flow.levels.${level}.sign`) })}</Text>
                      </li>
                    ))}
                  </ul>
                  <Text color="muted" variant="caption">{t('flow.howTo.scaleNote')}</Text>
                </Stack>
              </Card>
              <Card>
                <Stack gap="2">
                  <Heading level={3}>{t('flow.howTo.tipsTitle')}</Heading>
                  <ul className="lighting__tips">
                    {(['range', 'methods', 'pumps', 'corals', 'repeat'] as const).map((k) => (
                      <li key={k}><Text>{String(t(`flow.howTo.tips.${k}`))}</Text></li>
                    ))}
                  </ul>
                </Stack>
              </Card>
            </Stack>
          </Section>
          <Dialog isOpen={open} onClose={() => setOpen(false)} title={t('flow.new.dialogTitle')} size="md" data-testid="new-flow-survey-dialog">
            {open && (
              <NewFlowSurveyForm
                aquariumId={aquariumId}
                onCancel={() => setOpen(false)}
                onCreated={(survey) => {
                  setOpen(false);
                  void query.refetch();
                  navigate(`/aquariums/${encodeURIComponent(aquariumId)}/flow/${encodeURIComponent(survey.id)}`);
                }}
              />
            )}
          </Dialog>
        </PageContent>
      }
    />
  );
}

export default FlowPage;
